import React, { useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { INDIAN_STATES, getStateByCode } from '../../lib/states';
import { validateGSTIN } from '../../lib/gstin';
import { validatePhone, validateEmail } from '../../lib/validate';
import { Check, X, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';

export default function BusinessSection({ business, setBusiness }) {
  const { t } = useLanguage();
  const gstinCheck = useMemo(() => business.gstin ? validateGSTIN(business.gstin) : null, [business.gstin]);
  const requiredClass = (value) => String(value || '').trim()
    ? ''
    : 'border-red-200 bg-red-50/30 focus-visible:ring-red-200';

  // Auto-detect state from GSTIN (any 15-char format-valid GSTIN)
  React.useEffect(() => {
    if (!gstinCheck?.valid) return;
    const st = getStateByCode(gstinCheck.stateCode);
    if (st && st.short !== business.state) {
      setBusiness((b) => ({ ...b, state: st.short, stateName: st.name }));
    }
  }, [gstinCheck?.valid, gstinCheck?.stateCode]);

  const handleLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert(t('logoSizeError')); return; }
    const reader = new FileReader();
    reader.onload = () => setBusiness((b) => ({ ...b, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  const update = (patch) => setBusiness((b) => ({ ...b, ...patch }));

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm" data-testid="business-section">
      <div className="mb-4">
        <h3 className="font-heading text-lg font-bold text-slate-900">{t('yourBusiness')}</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 space-y-3">
          <div>
            <Label htmlFor="biz-name">{t('businessName')}</Label>
            <Input id="biz-name" data-testid="business-name-input" value={business.name} onChange={(e) => update({ name: e.target.value })} placeholder={t('businessName')} className={requiredClass(business.name)} />
          </div>
          <div>
            <Label htmlFor="biz-gstin">{t('gstin')}</Label>
            <div className="relative">
              <Input id="biz-gstin" data-testid="business-gstin-input" value={business.gstin} onChange={(e) => update({ gstin: e.target.value.toUpperCase() })} placeholder="22AAAAA0000A1Z5" maxLength={15} className="font-mono uppercase pr-8" />
              {business.gstin?.length === 15 && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  {gstinCheck?.valid
                    ? <Check className="w-4 h-4 text-emerald-600" data-testid="gstin-valid" />
                    : <X className="w-4 h-4 text-red-500" data-testid="gstin-invalid" />}
                </div>
              )}
            </div>
            {business.gstin?.length === 15 && !gstinCheck?.valid && (
              <p className="text-xs text-red-500 mt-1">{gstinCheck?.reason}</p>
            )}
          </div>
        </div>

        <div>
          <Label>{t('logo')}</Label>
          <label className="flex flex-col items-center justify-center h-[84px] border-2 border-dashed border-slate-200 rounded-md cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-colors" data-testid="logo-upload-label">
            {business.logo ? (
              <img src={business.logo} alt="logo" className="max-h-16 object-contain" />
            ) : (
              <div className="text-slate-400 text-xs text-center px-2">
                <Upload className="w-4 h-4 mx-auto mb-1" />
                {t('uploadLogo')}
              </div>
            )}
            <input type="file" accept="image/*" className="hidden" onChange={handleLogo} data-testid="logo-upload-input" />
          </label>
          {business.logo && (
            <Button variant="ghost" size="sm" className="w-full mt-1 text-xs h-7" onClick={() => update({ logo: '' })}>{t('remove')}</Button>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>{t('state')}</Label>
          <Select value={business.state} onValueChange={(v) => {
            const st = INDIAN_STATES.find((s) => s.short === v);
            update({ state: v, stateName: st?.name || '' });
          }}>
            <SelectTrigger data-testid="business-state-select">
              <SelectValue placeholder={t('selectState')} />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {INDIAN_STATES.map((s) => (
                <SelectItem key={s.short} value={s.short}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t('phone')}</Label>
          <Input
            data-testid="business-phone-input"
            value={business.phone || ''}
            onChange={(e) => update({ phone: e.target.value })}
            placeholder="+91 98765 43210"
            className={validatePhone(business.phone) ? 'border-red-400 focus-visible:ring-red-300' : ''}
          />
          {validatePhone(business.phone) && <p className="text-xs text-red-500 mt-1">{validatePhone(business.phone)}</p>}
        </div>
      </div>

      <div className="mt-4">
        <Label>{t('address')}</Label>
        <Textarea data-testid="business-address-input" rows={2} value={business.address} onChange={(e) => update({ address: e.target.value })} placeholder={t('streetPlaceholder')} />
      </div>

      <div className="mt-4">
        <Label>{t('email')}</Label>
        <Input
          type="email"
          data-testid="business-email-input"
          value={business.email || ''}
          onChange={(e) => update({ email: e.target.value })}
          placeholder="you@business.com"
          className={validateEmail(business.email) ? 'border-red-400 focus-visible:ring-red-300' : ''}
        />
        {validateEmail(business.email) && <p className="text-xs text-red-500 mt-1">{validateEmail(business.email)}</p>}
      </div>
    </section>
  );
}
